"use client";
import { FlexBetween, FlexBetweenCol, FlexCol } from "@/components";
import { cn } from "@/lib";
import CustomButton from "../ui/customButton";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useRouter } from "next/navigation";
import {
  IInquiriesComponentProps,
  TMilitaryServiceInquiryInfer,
} from "@/interfaces";
import { militaryServiceInquirySchema } from "@/validations";
import TextField from "../ui/textField";
import { useMilitaryServiceInquiryMutation } from "@/hooks/useInquiry";
import { useSession } from "next-auth/react";

import { useTranslations } from "next-intl";
import { routes } from "@/routes";
import { useModalStore } from "@/store";
import { landingQuickAccessNavigation } from "@/constants";
import { globalRawServicesData } from "@/constants/globalRawServicesData/globalRawServicesData";
import { useState } from "react";
import PaymentModalContainer from "@/components/Payment/paymentModalContainer";
import { globalRawServicesApiUnification } from "@/constants/globalRawServicesData/globalRawServicesApiUnification";
import { IMilitaryServiceInquiryMutationPayload } from "@/interfaces/inquiries/governmental";

const MilitaryServiceInquiry = ({
  openPaymentModal,
  isLoggedIn,
  navigateToLogin,
  isOpen,
  onClose,
}: IInquiriesComponentProps) => {
  const t = useTranslations();
  const { closeModal } = useModalStore();
  const { data: session } = useSession();

  const {
    control,
    handleSubmit,
    formState: { errors: formError },
    getValues,
  } = useForm<TMilitaryServiceInquiryInfer>({
    resolver: zodResolver(militaryServiceInquirySchema),
    defaultValues: {
      digit: "",
    },
  });
  const {
    isPending: isSubmittingMilitaryService,
    mutate: militaryServiceInquiryFn,
    error: militaryServiceInquiryError,
    reset: resetMilitaryServiceInquiry,
  } = useMilitaryServiceInquiryMutation(session?.user.token);
  const [packageId, setPackageId] = useState<string | null>(null);
  const handlePackageSelect = (packageId: string) => {
    setPackageId(packageId);
  };
  const router = useRouter();
  const militaryServiceInquiryDetails = landingQuickAccessNavigation.find(
    (item) => item.key === globalRawServicesData.Military_Service_Status.key,
  );
  const onSubmit = (data: any) => {
    if (!isLoggedIn) {
      navigateToLogin();
      return;
    }
    openPaymentModal();
  };

  const paymentSubmission = () => {
    const formData = getValues();
    const payload: Readonly<IMilitaryServiceInquiryMutationPayload> = {
      national_code: formData.digit,
      birth_date: formData.birth_date,
      campaign: packageId || undefined,
    };
    militaryServiceInquiryFn(
      { payload },
      {
        onSuccess: (data) => {
          const purchase_id = data.data?.purchase_id;
          if (purchase_id) {
            router.push(
              `${routes.main.INQUIRY_DETAILS.primaryPath}/${data.data?.purchase_id}`,
            );
            closeModal();
          }
        },
      },
    );
  };

  return (
    <>
      <FlexBetween className="items-stretch gap-8 lg:gap-12">
        <form
          onSubmit={handleSubmit(onSubmit)}
          className={cn("flex-center basis-full sm:basis-1/2")}
        >
          <FlexBetweenCol gap={6} className={cn("w-full xl:gap-8")}>
            <p className={cn("text-primary-main text-sm xl:text-base")}>
              {t("inquiries.militaryService.formTitle")}
            </p>
            <Controller
              name="digit"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  aria-label="national_code"
                  hasShadow
                  maxLength={10}
                  numeric
                  inputMode="numeric"
                  placeholder={t("general.nationalCode")}
                  inputClassName={cn("tracking-widest dir-ltr h-10")}
                  error={formError.digit?.message}
                />
              )}
            />
            <CustomButton
              id="landing-military-service-submit"
              type="submit"
              className="mt-2 w-max lg:mt-4"
              onClick={handleSubmit(onSubmit)}
            >
              {t("inquiries.militaryService.submitButton")}
            </CustomButton>
          </FlexBetweenCol>
        </form>

        <FlexCol
          gap={2}
          className={cn("hidden basis-1/2 sm:flex lg:gap-4 xl:gap-6")}
        >
          <h2 className={cn("text-sm font-bold lg:text-base xl:text-xl")}>
            {t("inquiries.militaryService.headerTitle")}
          </h2>
          <p
            className={cn(
              "text-custom-gray-200 text-justify text-xs leading-5 whitespace-pre-line lg:text-sm lg:leading-normal xl:text-base",
            )}
          >
            {t("inquiries.militaryService.description")}
          </p>
        </FlexCol>
      </FlexBetween>
      {isOpen && (
        <PaymentModalContainer
          payBtnId="landing-payment-military-service"
          submissionLoading={isSubmittingMilitaryService}
          submissionError={militaryServiceInquiryError}
          paymentSubmission={paymentSubmission}
          isOpen={isOpen}
          onClose={onClose}
          showData="service"
          serviceId={
            globalRawServicesApiUnification.Military_Service_Status.apiId
          }
          selectedService={militaryServiceInquiryDetails}
          onErrorReset={resetMilitaryServiceInquiry}
          onPackageSelect={handlePackageSelect}
        />
      )}
    </>
  );
};

export default MilitaryServiceInquiry;
